import React, { createContext, useCallback, useContext, useState } from 'react';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';

const AdminConfirmContext = createContext(null);

/**
 * Asks the admin to confirm a change, with one shared ConfirmDialog for an admin page.
 * Wrap the page in it; useAdminConfirm() returns confirm({ title, content, confirmText }),
 * which resolves to true (Confirm) or false (Cancel):
 *
 *   const confirmed = await confirm({ title: 'Turn x off for everyone?' });
 *   if (!confirmed) return;
 */
export const AdminConfirmProvider = ({ children }) => {
	// The last request stays set while the dialog closes, so its text doesn't change mid-close
	const [request, setRequest] = useState({ open: false });

	const confirm = useCallback(
		(options) => new Promise((resolve) => setRequest({ ...options, open: true, resolve })),
		[],
	);

	const finish = (confirmed) => {
		request.resolve(confirmed);
		setRequest((previous) => ({ ...previous, open: false }));
	};

	return (
		<AdminConfirmContext.Provider value={confirm}>
			{children}
			<ConfirmDialog
				openDialog={request.open}
				handleClose={() => finish(false)}
				handleConfirm={() => finish(true)}
				title={request.title}
				content={request.content}
				confirmText={request.confirmText}
				canBeUndone
			/>
		</AdminConfirmContext.Provider>
	);
};

export const useAdminConfirm = () => useContext(AdminConfirmContext);
